import React, { useEffect, useRef, useState } from "react";
import { Box, Card, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import PageButton from "src/components/PageButton";
import FormInput from "src/components/FormFields/FormFields";
import { useCheckMobileMutation, useSignUpMutation } from "src/reduxServer/services/auth/auth";
import { useTheme } from "@mui/material";
import { showToast } from "src/utils/toastfy";
import PageTypography from "src/components/PageTypography/PageTypography";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useGetCityQuery } from "src/reduxServer/services/userAccount";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "src/utils/firebase/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { RootState } from "src/reduxServer/store";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setLanguage } from "src/reduxServer/slices/languageSlice";
import i18n from "src/utils/rtl";
import { formatSaudiMobile } from "src/utils/help";

declare global {
    interface Window {
        recaptchaVerifier: RecaptchaVerifier;
    }
}



const COUNTRY_CODE = "+966";
const OTP_LENGTH = 6;

type RegisterFormValues = {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    gender: string;
    address: string;
    city: string;
};

const SingUp = () => {
    const [step, setStep] = useState(1);
    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [confirmation, setConfirmation] = useState<any>(null);
    const [showPassword, setShowPassword] = useState(false)
    const [loading, setLoading] = useState(false);
    const theme = useTheme()
    const navigate = useNavigate();
    const { t } = useTranslation();
    const lng = useSelector((state: RootState) => state.language.lang) || "en";
    const dispatch = useDispatch();

    const [checkMobile, { isLoading }] = useCheckMobileMutation();
    const [signUpUser, { isLoading: isSignUpLoading, error }] = useSignUpMutation();
    const { data: cityList, } = useGetCityQuery();
    console.log("city List>>>", cityList?.data)

    const schema = Yup.object({
        first_name: Yup.string().required(t("firstName_required")),
        last_name: Yup.string().required(t("lastName_required")),
        email: Yup.string().email(t("invalid_Email")).required(t("email_required")),
        gender: Yup.string().required(t("gender_required")),
        password: Yup.string().required(t("password_required")),
        address: Yup.string().required(t("address_required")),
        city: Yup.string().required(t("city_required")),


    }).required();


    const { control, handleSubmit } = useForm<RegisterFormValues>({
        resolver: yupResolver(schema) as any

    });

    const inputRefs = Array.from({ length: 6 }, () =>
        useRef<HTMLInputElement>(null)
    );

    const recaptchaRef = useRef<RecaptchaVerifier | null>(null);


    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    }

    const changeLang = (lng) => {
        dispatch(setLanguage(lng));
        i18n.changeLanguage(lng);   // update i18next
    };

    useEffect(() => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(
                auth,
                "recaptcha-container",
                {
                    size: "invisible",
                }
            );

            window.recaptchaVerifier.render();
        }
    }, []);




    // ✅ Step 2: OTP Handler
    const handleOtpInput = (value: string, index: number) => {

        if (!/^\d?$/.test(value)) return;

        const otpArray = otp.split("");
        otpArray[index] = value;
        const updatedOtp = otpArray.join("");
        setOtp(updatedOtp);

        if (value && index < 5) inputRefs[index + 1].current?.focus();


    };


    const handleMobileSubmit = async () => {

        if (!mobile || mobile.length < 9) {
            showToast("error", t("Enter valid mobile number"));
            return;
        }

        const fullPhone = formatSaudiMobile(mobile);
        try {
            setLoading(true);
            // 1️⃣ First call backend API
            const apiResponse = await checkMobile({
                mobile: fullPhone,
            }).unwrap();


            if (apiResponse?.code !== 200) {
                setLoading(false);
                showToast("error", apiResponse?.msg || "Mobile not allowed");
                return;
            }




            const appVerifier = window.recaptchaVerifier;;

            if (!appVerifier) {
                showToast("error", "Recaptcha not ready");
                return;
            }

            const confirmationResult = await signInWithPhoneNumber(
                auth,
                fullPhone,
                appVerifier
            );




            setConfirmation(confirmationResult);
            setStep(2);

            showToast("success", t("OTP sent successfully"));

        } catch (error: any) {
            console.log("FULL ERROR OBJECT:", error);
            console.log("ERROR CODE:", error.code);
            console.log("ERROR MESSAGE:", error.msg);
            setLoading(false);
            showToast("error", error?.data?.msg || error.msg || error?.code);

        }
    };

    // ---------------- STEP 2: VERIFY OTP ----------------
    const handleOtpSubmit = async () => {
        try {
             setLoading(true);
            if (otp.length !== 6) {
                showToast("error", t("Enter 6 digit OTP"));
                setLoading(false);
                return;
            }
            if (!confirmation) return;
            await confirmation.confirm(otp);

            showToast("success", t("OTP verified successfully"));
             setLoading(false);
            setStep(3);

        } catch (error) {
            setLoading(false);
            showToast("error", t("Invalid OTP"));
        }
    };


    // ---------------- STEP 3: REGISTER ----------------
    const onSubmit = async (data: RegisterFormValues) => {
        const payload = {
            first_name: data.first_name,
            last_name: data.last_name,
            mobile: formatSaudiMobile(mobile),
            email: data?.email?.toLowerCase(),
            password: data.password,
            gender: data.gender,
            address: data.address,
            city: data.city,
        };

        try {
            const res: any = await signUpUser(payload).unwrap();

            if (res.code === 200) {
                showToast("success", t(res.msg) || t("Account created successfully"));
                navigate("/account/login");
            } else {
                showToast("error", t(res.msg) || t("SignUp failed"));
            }
        } catch (err: any) {
            showToast("error", t(err?.data?.msg) || t("Invalid Details"));
        }
    };



    const heading = step === 1 ? t("verify your phone number") : step === 2 ? t("OTP Verification") : step === 3 ? (t("Create Your Account")) : "";

    return (
        <>

            <Box

                display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Card sx={{ width: 450, p: 4 }}>


                    <Box sx={{ textAlign: "center", }} mb={3}>
                        <PageTypography variant="h4">{heading}</PageTypography>
                    </Box>
                    {/* STEP 1 */}
                    {step === 1 && (
                        <>


                            <TextField
                                fullWidth
                                name="mobile"
                                label={t("Enter Mobile Number")}
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            +966
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <PageButton
                                fullWidth

                                onClick={handleMobileSubmit}
                                // disabled={mobile.length !== 10}
                                disabled={loading}
                                loading={loading}
                                sx={{
                                    mt: 3,
                                    py: 1.3,
                                    fontSize: "1rem",
                                    borderRadius: 1,
                                    textTransform: "none",
                                    backgroundColor: theme.palette.primary.main,
                                    ":hover": { backgroundColor: theme.palette.primary.dark }
                                }}
                            >
                                {t("Send OTP")}
                            </PageButton>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    margin: 2,
                                }}>
                                <PageButton variant={lng === "ar" ? "contained" : "outlined"}
                                    sx={{
                                        marginLeft: 2
                                    }}
                                    onClick={() => changeLang("ar")}
                                >
                                    العربية
                                </PageButton>
                                <PageButton variant={lng === "en" ? "contained" : "outlined"}
                                    onClick={() => changeLang("en")}
                                    sx={{
                                        marginLeft: 2
                                    }}
                                >
                                    English
                                </PageButton>
                            </Box>
                        </>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                        <>


                            <Stack direction="row" spacing={2} justifyContent="center">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <TextField
                                        key={index}
                                        inputRef={inputRefs[index]}
                                        value={otp[index] || ""}
                                        onChange={(e) =>
                                            handleOtpInput(e.target.value.replace(/\D/g, ""), index)
                                        }
                                        inputProps={{
                                            maxLength: 1,
                                            style: { textAlign: "center", fontSize: "22px" },
                                        }}
                                        sx={{ width: 50 }}
                                    />
                                ))}
                            </Stack>


                            <PageButton
                                fullWidth

                                onClick={handleOtpSubmit}
                                disabled={otp.length !== OTP_LENGTH}
                                loading={loading}
                                sx={{
                                    mt: 3,
                                    py: 1.3,
                                    fontSize: "1rem",
                                    borderRadius: 1,
                                    textTransform: "none",
                                    backgroundColor: theme.palette.primary.main,
                                    ":hover": { backgroundColor: theme.palette.primary.dark }
                                }}
                            >
                                {t("Verify OTP")}
                            </PageButton>
                        </>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <FormInput
                                name="first_name"
                                label={t("First Name")}
                                control={control}
                                rules={undefined}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />

                            <FormInput
                                name="last_name"
                                label={t("Last Name")}
                                control={control}
                                rules={undefined}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />
                            <FormInput
                                name="mobile"
                                label={t("Mobile")}
                                control={control}
                                rules={undefined}
                                value={COUNTRY_CODE + mobile}
                                disabled
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />

                            <FormInput
                                name="email"
                                label={t("Email")}
                                control={control}
                                rules={undefined}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />

                            <FormInput
                                name="password"
                                label={t("passWord")}
                                control={control}
                                type={showPassword ? "text" : "password"}
                                rules={undefined}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleTogglePassword} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <FormInput
                                name="gender"
                                label={t("Gender")}
                                control={control}
                                rules={undefined}
                                select
                                options={
                                    [
                                        { label: t("Male"), value: "M" },
                                        { label: t("Female"), value: "F" }
                                    ]
                                }
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />

                            <FormInput
                                name="address"
                                label={t("Address")}
                                control={control}
                                rules={undefined}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />

                            <FormInput
                                name="city"
                                label={t("City")}
                                control={control}
                                rules={undefined}
                                select
                                options={cityList?.data?.map((city) => ({
                                    label: lng === "ar" ? city.name_ar : city.name_en,
                                    value: city.id
                                }))}
                                sx={{
                                    mb: 2,
                                    "& .MuiInputBase-root": {
                                        backgroundColor: theme.palette.background.default
                                    }
                                }}
                            />



                            <PageButton fullWidth
                                type="submit"
                                loading={isSignUpLoading}
                                sx={{
                                    mt: 1,
                                    py: 1.3,
                                    fontSize: "1rem",
                                    borderRadius: 1,
                                    textTransform: "none",
                                    backgroundColor: theme.palette.primary.main,
                                    ":hover": { backgroundColor: theme.palette.primary.dark }
                                }}>
                                {t("Sign_up")}
                            </PageButton>
                        </form>
                    )}



                </Card>
            </Box>
            <div id="recaptcha-container"></div>
        </>
    );
};

export default SingUp;

